import React from 'react';

export const FireLoader = ({ fullScreen = true, size = 'md', text = 'LOADING...' }) => {
  const sizes = {
    sm: { container: 'w-48 h-48', svgSize: 180, r: 70, stroke: 3, text: 'text-xs' },
    md: { container: 'w-56 h-56', svgSize: 200, r: 80, stroke: 3.5, text: 'text-sm' },
    lg: { container: 'w-64 h-64', svgSize: 220, r: 88, stroke: 4, text: 'text-base' },
  };

  const config = sizes[size] || sizes.md;

  const containerClasses = fullScreen
    ? 'fixed inset-0 flex items-center justify-center bg-white/30 backdrop-blur-[1px] z-50'
    : 'flex items-center justify-center w-full h-full min-h-[200px]';

  // Calculate circumference for smooth animation
  const circumference = 2 * Math.PI * config.r;
  const strokeDashoffset = circumference * 0.75; // 75% of circle visible

  return (
    <div className={containerClasses}>
      <div className={`relative ${config.container} flex items-center justify-center`}>
        
        {/* Spinner Container */}
        <div className="absolute inset-0 flex items-center justify-center">
          <svg
            width={config.svgSize}
            height={config.svgSize}
            viewBox="0 0 160 160"
            className="animate-spin"
            style={{ 
              animationDuration: '1.2s',
              animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {/* Light Background Circle - Ultra thin */}
            <circle
              cx="80"
              cy="80"
              r={config.r}
              fill="none"
              stroke="rgba(229, 231, 235, 0.3)"
              strokeWidth={config.stroke}
            />

            {/* Smooth Orange Spinning Arc */}
            <circle
              cx="80"
              cy="80"
              r={config.r}
              fill="none"
              stroke="#1680f9"
              strokeWidth={config.stroke}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transition: 'stroke-dashoffset 0.3s ease',
              }}
            />
          </svg>
        </div>

        {/* LOADING Text - Clean and minimal */}
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <span 
            className={`
              ${config.text} 
              font-medium 
              tracking-[0.4em] 
              text-gray-400
              text-center
              uppercase
            `}
            style={{ 
              letterSpacing: '0.4em',
              fontWeight: 500
            }}
          >
            {text}
          </span>
        </div>
      </div>
    </div>
  );
};

// Other components
export const SpinnerLoader = ({ size = 'md', color = 'primary' }) => {
  const sizes = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  const colors = {
    primary: 'border-primary-500',
    secondary: 'border-secondary-500',
    white: 'border-white',
    gray: 'border-gray-500',
  };

  return (
    <div className="flex items-center justify-center">
      <div
        className={`${sizes[size]} border-4 ${colors[color]} border-t-transparent rounded-full animate-spin`}
      />
    </div>
  );
};

export const PageLoader = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <FireLoader fullScreen={false} size="lg" text="LOADING..." />
    </div>
  );
};

export const InlineLoader = ({ size = 'sm', color = 'primary' }) => {
  return (
    <div className="inline-flex items-center">
      <SpinnerLoader size={size} color={color} />
    </div>
  );
};

// Advanced smooth loader with gradient
export const GradientLoader = ({ fullScreen = true, text = 'LOADING...' }) => {
  const containerClasses = fullScreen
    ? 'fixed inset-0 flex items-center justify-center bg-white/30 backdrop-blur-[1px] z-50'
    : 'flex items-center justify-center w-full h-full min-h-[200px]';

  return (
    <div className={containerClasses}>
      <div className="relative w-64 h-64 flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center">
          <svg
            width="220"
            height="220"
            viewBox="0 0 160 160"
            className="animate-spin"
            style={{ animationDuration: '1.5s' }}
          >
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f97316" />
                <stop offset="50%" stopColor="#fb923c" />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
            </defs>
            
            {/* Background */}
            <circle
              cx="80"
              cy="80"
              r="70"
              fill="none"
              stroke="rgba(229, 231, 235, 0.2)"
              strokeWidth="3"
            />
            
            {/* Gradient Arc */}
            <circle
              cx="80"
              cy="80"
              r="70"
              fill="none"
              stroke="url(#gradient)"
              strokeWidth="3.5"
              strokeDasharray="439.82"
              strokeDashoffset="329.86"
              strokeLinecap="round"
            />
          </svg>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <span className="text-sm font-medium tracking-[0.4em] text-gray-400 uppercase">
            {text}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FireLoader;